import type { LocatorJson } from "@msb/reader-bridge";

/*
 * Annotations : passages surlignés, avec la note personnelle du lecteur. Gardées dans ce navigateur et, avec un
 * compte, synchronisées comme les signets (`bookSync.ts`).
 * Même modèle que Studio (dépôt ai, `nextjs/src/reader/annotations.ts`).
 */

export type HighlightColor = "green" | "blue" | "pink";

export interface Annotation {
  id: string;
  /** Locator Readium : le fichier, la position, et le texte avec son contexte (`text.highlight` est le passage). */
  locator: LocatorJson;
  color: HighlightColor;
  note?: string;
  createdAt: string;
  updatedAt?: string;
}

// Teintes opaques : Readium écrit le texte surligné en noir ou en blanc selon la teinte, et Safari jugeait
// sombres les teintes translucides. Pas de jaune : c'est la couleur des passages cités et de la recherche.
export const HIGHLIGHT_COLORS: Array<{ id: HighlightColor; tint: string }> = [
  { id: "green", tint: "rgb(191, 237, 184)" },
  { id: "blue", tint: "rgb(179, 220, 255)" },
  { id: "pink", tint: "rgb(255, 191, 223)" },
];

export const tintOf = (color: HighlightColor) => HIGHLIGHT_COLORS.find((c) => c.id === color)?.tint ?? HIGHLIGHT_COLORS[0]!.tint;

export const MAX_NOTE = 2000;
const key = (bookId: string) => `msb-reader:annotations:${bookId}`;
// Un surlignage jaune enregistré par Studio s'affiche en vert.
const isColor = (value: unknown): value is HighlightColor => value === "green" || value === "blue" || value === "pink";

/** Dans l'ordre de lecture : le fichier, puis la place dans le fichier. */
export function sortAnnotations(annotations: Annotation[]): Annotation[] {
  const place = (a: Annotation) => a.locator.locations?.totalProgression ?? a.locator.locations?.progression ?? 0;
  return [...annotations].sort((x, y) => place(x) - place(y) || x.createdAt.localeCompare(y.createdAt));
}

export function parseAnnotations(raw: unknown): Annotation[] {
  if (!Array.isArray(raw)) return [];
  return sortAnnotations(
    raw.flatMap((item): Annotation[] => {
      if (!item || typeof item !== "object") return [];
      const a = item as Partial<Annotation> & { color?: unknown };
      if (typeof a.id !== "string" || typeof a.locator?.href !== "string" || typeof a.createdAt !== "string") return [];
      return [{ ...(a as Annotation), color: isColor(a.color) ? a.color : "green" }];
    }),
  );
}

export function loadAnnotations(bookId: string): Annotation[] {
  try {
    const raw = localStorage.getItem(key(bookId));
    return raw ? parseAnnotations(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

export function saveAnnotations(bookId: string, annotations: Annotation[]): void {
  try {
    localStorage.setItem(key(bookId), JSON.stringify(annotations));
  } catch {
    // Stockage indisponible (navigation privée, quota) : les annotations restent le temps de la lecture.
  }
}

export function createAnnotation(locator: LocatorJson, color: HighlightColor, note?: string, now = new Date()): Annotation {
  const id = `${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const text = note?.trim().slice(0, MAX_NOTE);
  return { id, locator, color, ...(text ? { note: text } : {}), createdAt: now.toISOString(), updatedAt: now.toISOString() };
}

export function updateAnnotation(
  annotations: Annotation[],
  id: string,
  changes: { color?: HighlightColor; note?: string },
  now = new Date(),
): Annotation[] {
  return annotations.map((annotation) => {
    if (annotation.id !== id) return annotation;
    const next: Annotation = { ...annotation, updatedAt: now.toISOString() };
    if (changes.color) next.color = changes.color;
    if ("note" in changes) {
      const note = (changes.note ?? "").trim().slice(0, MAX_NOTE);
      if (note) next.note = note;
      else delete next.note;
    }
    return next;
  });
}

export const removeAnnotation = (annotations: Annotation[], id: string) => annotations.filter((annotation) => annotation.id !== id);
